/* ========================================================================== *\
___________       __         .__
\_   _____/ _____/  |________|__| ____
 |    __)_ /    \   __\_  __ \  |/  _ \
 |        \   |  \  |  |  | \/  (  <_> )
/_______  /___|  /__|  |__|  |__|\____/
        \/     \/
\* ========================================================================== */
// ส่วนประกอบการ์ดผลงานภาพ ใช้ร่วม 4 หน้า (ภาพ AI · กราฟิกแคมเปญ · จัดการภาพ · คิวงานภาพ) — 18 ต.ค. 2569
// กดที่รูป = ตรวจภาพ · ป้ายบนรูปขึ้นเฉพาะสถานะที่ต้องรู้ · ปุ่มรองอยู่ในเมนู ⋯ ให้ปุ่มทั้งหมดอยู่แถวเดียว
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import ButtonBase from '@mui/material/ButtonBase';
import Icon from '@mui/material/Icon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';

// ปุ่มไอคอนในแถวท้ายการ์ดสูงเท่าปุ่มหลัก (MDButton size small ไอคอนล้วนเตี้ยกว่า 7px)
export const iconBtnSx = { width: 32, height: 32, minWidth: 32, minHeight: 32 };

const overlayChip = {
  position: 'absolute',
  px: 0.75,
  borderRadius: 1,
  bgcolor: 'rgba(255,255,255,0.92)',
  color: 'text.secondary',
  fontSize: '0.75rem',
  lineHeight: 1.7,
};

/* select = { checked, onToggle, label } → โหมดเลือกหลายภาพ: กดที่รูป = ติ๊ก (แทนตรวจภาพ) · มีช่องติ๊กมุมบนขวา (A2 · 25 ต.ค. 2569) */
export function ReviewThumb({ onReview, label, badge, meta, sx, select, children }) {
  const base = {
    position: 'relative',
    width: '100%',
    overflow: 'hidden',
    ...sx,
    ...(select && select.checked
      ? { outline: '3px solid', outlineColor: 'info.main', outlineOffset: -3 }
      : {}),
  };
  const overlay = (
    <>
      {select && (
        <MDBox
          component='span'
          aria-hidden='true'
          sx={{
            ...overlayChip,
            top: 8,
            right: 8,
            px: 0,
            width: 28,
            height: 28,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: select.checked ? 'info.main' : 'text.secondary',
          }}
        >
          <Icon fontSize='small'>{select.checked ? 'check_box' : 'check_box_outline_blank'}</Icon>
        </MDBox>
      )}
      {badge && (
        <MDBox sx={{ position: 'absolute', top: 8, left: 8, display: 'flex', gap: 0.5 }}>
          {badge}
        </MDBox>
      )}
      {meta && (
        <MDBox component='span' sx={{ ...overlayChip, left: 8, bottom: 8 }}>
          {meta}
        </MDBox>
      )}
      {onReview && (
        <MDBox
          component='span'
          sx={{
            ...overlayChip,
            right: 8,
            bottom: 8,
            px: 0,
            width: 28,
            height: 28,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon fontSize='small'>zoom_in</Icon>
        </MDBox>
      )}
    </>
  );
  if (select) {
    return (
      <ButtonBase
        onClick={select.onToggle}
        aria-label={select.label}
        aria-pressed={select.checked}
        sx={{ ...base, cursor: 'pointer' }}
      >
        {children}
        {overlay}
      </ButtonBase>
    );
  }
  if (!onReview) {
    return (
      <MDBox sx={base}>
        {children}
        {overlay}
      </MDBox>
    );
  }
  return (
    <ButtonBase onClick={onReview} aria-label={label} sx={{ ...base, cursor: 'zoom-in' }}>
      {children}
      {overlay}
    </ButtonBase>
  );
}
ReviewThumb.defaultProps = {
  onReview: null,
  label: 'ตรวจภาพ',
  badge: null,
  meta: '',
  sx: {},
  select: null,
};
ReviewThumb.propTypes = {
  onReview: PropTypes.func,
  label: PropTypes.string,
  badge: PropTypes.node,
  meta: PropTypes.string,
  sx: PropTypes.objectOf(PropTypes.any),
  select: PropTypes.shape({
    checked: PropTypes.bool.isRequired,
    onToggle: PropTypes.func.isRequired,
    label: PropTypes.string.isRequired,
  }),
  children: PropTypes.node.isRequired,
};

/* ปุ่มดาวน์โหลดภาพเดียว ท้ายการ์ด (ไอคอน + aria-label ตามกติกาแถวเดียว) */
export function DownloadButton({ onClick, label, disabled }) {
  return (
    <MDButton
      size='small'
      variant='outlined'
      color='dark'
      iconOnly
      title='ดาวน์โหลดภาพนี้'
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      sx={iconBtnSx}
    >
      <Icon>download</Icon>
    </MDButton>
  );
}
DownloadButton.defaultProps = { disabled: false };
DownloadButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
};

/* แถบเลือกหลายภาพ → ดาวน์โหลดเป็น zip เดียว · วางที่หัวรายการผลงาน
   on = เปิดโหมดเลือก · selected/total = จำนวน · progress = [done, total] ระหว่างโหลด */
export function SelectBar({
  on,
  onToggle,
  selected,
  total,
  onAll,
  onNone,
  onDownload,
  busy,
  progress,
  pageSelected,
}) {
  /* หน้าที่แบ่งหน้า (คิวงาน) เลือกข้ามหน้าได้ — "ทั้งหมด" ดูเฉพาะหน้านี้ (รีเช็ค 26 ต.ค.: เดิมเทียบยอดรวมทุกหน้ากับยอดหน้านี้ หน้า 2 กดไม่ได้) */
  const onPage = pageSelected === null ? selected : pageSelected;
  if (!on) {
    return (
      <MDButton
        size='small'
        variant='outlined'
        color='dark'
        disabled={total === 0}
        onClick={onToggle}
        aria-label='เลือกหลายภาพเพื่อดาวน์โหลด'
      >
        <Icon>checklist</Icon>&nbsp;เลือกหลายภาพ
      </MDButton>
    );
  }
  return (
    <MDBox
      display='flex'
      alignItems='center'
      gap={0.75}
      flexWrap='wrap'
      role='group'
      aria-label='ดาวน์โหลดหลายภาพ'
    >
      <MDButton
        size='small'
        variant='text'
        color='dark'
        disabled={busy || onPage >= total}
        onClick={onAll}
      >
        ทั้งหมด
      </MDButton>
      <MDButton
        size='small'
        variant='text'
        color='dark'
        disabled={busy || selected === 0}
        onClick={onNone}
      >
        ล้าง
      </MDButton>
      <MDButton
        size='small'
        variant='gradient'
        color='info'
        disabled={busy || selected === 0}
        onClick={onDownload}
        aria-label={`ดาวน์โหลด ${selected} ภาพเป็นไฟล์ zip`}
      >
        <Icon>download</Icon>&nbsp;
        {busy && progress
          ? `กำลังโหลด ${progress[0]}/${progress[1]}…`
          : `ดาวน์โหลด ${selected} ภาพ (zip)`}
      </MDButton>
      <MDButton size='small' variant='outlined' color='dark' disabled={busy} onClick={onToggle}>
        เสร็จ
      </MDButton>
    </MDBox>
  );
}
SelectBar.defaultProps = { busy: false, progress: null, pageSelected: null };
SelectBar.propTypes = {
  on: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  selected: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  onAll: PropTypes.func.isRequired,
  onNone: PropTypes.func.isRequired,
  onDownload: PropTypes.func.isRequired,
  busy: PropTypes.bool,
  progress: PropTypes.arrayOf(PropTypes.number),
  pageSelected: PropTypes.number, // เลือกไว้กี่ภาพในหน้านี้ (หน้าที่แบ่งหน้า) · ไม่ส่ง = ใช้ selected
};

// items: [{ label, icon, onClick, danger, disabled }] — ใส่ false ได้ (ไม่แสดง) · ไม่มีรายการ = ไม่แสดงปุ่ม
export function CardMenu({ items, label }) {
  const [anchor, setAnchor] = useState(null);
  const shown = items.filter(Boolean);
  if (!shown.length) return null;
  return (
    <>
      <MDButton
        size='small'
        variant='outlined'
        color='dark'
        iconOnly
        aria-label={label}
        aria-haspopup='menu'
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={iconBtnSx}
      >
        <Icon>more_horiz</Icon>
      </MDButton>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {shown.map((it) => (
          <MenuItem
            key={it.label}
            disabled={it.disabled}
            onClick={() => {
              setAnchor(null);
              it.onClick();
            }}
            sx={it.danger ? { color: 'error.main' } : undefined}
          >
            <Icon fontSize='small' sx={{ mr: 1 }}>
              {it.icon}
            </Icon>
            {it.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
CardMenu.defaultProps = { label: 'ตัวเลือกเพิ่มเติม' };
CardMenu.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        icon: PropTypes.string.isRequired,
        onClick: PropTypes.func.isRequired,
        danger: PropTypes.bool,
        disabled: PropTypes.bool,
      }),
    ])
  ).isRequired,
  label: PropTypes.string,
};

// แถวปุ่มท้ายการ์ด ไม่ตัดขึ้นบรรทัดใหม่ · ปุ่มหลักใส่ sx={{ flexGrow: 1 }} ให้กว้างเต็มที่เหลือ
// การ์ดแคบ (< 250px · จอ ≤1440 ที่มีไอคอน 3 ตัว) ข้อความปุ่มหลักเคยตกเป็น 2 บรรทัด (เจ้าของงานทัก 25 ต.ค. 2569)
// → container query: ซ่อนไอคอนนำหน้าของปุ่มหลัก (ปุ่มหลักใส่ data-primary='' · GalleryLink ใส่ให้แล้ว) · ไอคอนรองย่อเป็น 28px · ข้อความไม่ตัดบรรทัด
export function ActionRow({ children }) {
  return (
    <MDBox
      mt='auto'
      display='flex'
      gap={0.75}
      alignItems='center'
      sx={{
        containerType: 'inline-size',
        /* เบราว์เซอร์เก่าที่ไม่รู้จัก container query ให้ตกบรรทัดแบบเดิม ดีกว่าข้อความล้นทับไอคอน */
        '@supports (container-type: inline-size)': {
          /* GalleryLink ห่อปุ่มไว้ใน div[data-primary] — ต้องเลือกปุ่มข้างในด้วย (รีเช็ค 26 ต.ค.) */
          '& > .MuiButton-root, & > [data-primary] .MuiButton-root': { whiteSpace: 'nowrap' },
          /* gap ของกล่องนี้เปลี่ยนใน @container ไม่ได้ (query ใช้กับลูกเท่านั้น) จึงไม่ตั้ง */
          '@container (max-width: 250px)': {
            '& > [data-primary] .MuiIcon-root': { display: 'none' },
            '& > .MuiButton-root[data-primary], & > [data-primary] .MuiButton-root': { px: 0.5 },
            '& > .MuiButton-root:not([data-primary])': {
              width: 28,
              minWidth: 28,
              height: 28,
              minHeight: 28,
            },
          },
        },
      }}
    >
      {children}
    </MDBox>
  );
}
ActionRow.propTypes = { children: PropTypes.node.isRequired };
